import { pdiMeasurementVerdict } from '@felix/domain';
import type { Content, TableCell, TDocumentDefinitions } from 'pdfmake/interfaces.js';
import {
  type ReportAttachment,
  type ReportFiles,
  reportAttachmentSection,
} from '../../attachments/report-files.js';
import { formatDay } from '../../format.js';
import { safeFileBase } from '../drills/report-pdf.js';
import { sha256Hex } from '../fuel/period-package.js';
import {
  pdfBlock,
  pdfFacts,
  pdfHeading,
  pdfLtUtc,
  pdfTable,
  pdfText,
  type RecordPdfApproval,
  type RecordPdfT,
  recordPdf,
} from '../reports/record-pdf.js';
import type { ReportBrand } from '../voyage/source.js';
import { checkpointRows, latestByCode, vesselNotApplicable } from './inspection-view.js';
import type {
  DrydockCloseout,
  DrydockJobRow,
  DrydockProject,
  DrydockScope,
  PdiFindingRow,
  PdiInspectionWork,
} from './types.js';

/**
 * Pre-Docking raporları (ADR 0129 Ek Faz 5; Excel Screen 10 "Inspection Report", 11 "Yard Job
 * List", 12 "Close Project"): kayıtların kendisinden, ortak kayıt PDF kabuğunda (ADR 0062 §3).
 * Yeni tablo yok; rapor türetilir, saklanmaz. İstemcide, internetsiz üretilir.
 *
 * - **Denetim raporu**: genel bilgi, özet sayılar, her kontrol noktasının son sonucu (erişim,
 *   denetçi, zaman LT + UTC, revizyon, inceleme), ölçümler (sınır kararıyla), düzeltmeler
 *   (gerekçesiyle), bulgular ve kanıtlar (resim gömülü, diğer dosya bağlantı; ADR 0130 §A).
 *   Açık denetim "INSPECTION OPEN – PROVISIONAL" işaretlidir.
 * - **Yard Job List**: projenin işleri; ofis onayı olmayan iş satırında ve başlıkta "NOT APPROVED"
 *   (ajan kararı, ADR 0129 Ek Faz 5: liste onaysız işi gizlemez, işaretler). Durum Approved / In Progress /
 *   Completed / Verified / Closed / Pending; ek iş, onaydan önce başlayan ve taşınan işler
 *   işaretli; iptal edilen işler ayrı tabloda, gerekçesiyle.
 * - **Kapanış raporu**: proje, denetimler, işler, bekleyen ve taşınan işler, kapanmamış bulgular,
 *   ofis doğrulamaları. Kapanmamış projede "PREVIEW – PROJECT NOT CLOSED" ve kapanış planı.
 *
 * Her raporun sonunda içeriğin özeti (JCS benzeri sıralı anahtarlı JSON + SHA-256): basım
 * zamanından değil kayıtlardan hesaplanır, aynı kayıtlar aynı özeti verir [NR496 4.6.3].
 */

const PREFIX = 'drydockReport';

/** Sıralı anahtarlı JSON (RFC 8785 JCS'nin bu veri için yeterli alt kümesi: metin, sayı, mantık). */
export function canonicalJson(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

/** Rapor içeriğinin özeti: SHA-256 (onaltılı) sıralı anahtarlı JSON üzerinden. */
export function drydockReportHash(data: unknown): string {
  return sha256Hex(new TextEncoder().encode(canonicalJson(data)));
}

/** Dosya adı: `pre-docking-inspection-DD-2026-001-PDI-1.pdf` vb. (dil bağımsız, ASCII). */
export function drydockReportFileName(kind: 'pdi' | 'jobs' | 'closeout', no: string): string {
  const base = {
    pdi: 'pre-docking-inspection',
    jobs: 'yard-job-list',
    closeout: 'closeout-report',
  };
  return `${safeFileBase(`${base[kind]}-${no}`)}.pdf`;
}

const helpers = (t: RecordPdfT) => ({
  f: (key: string) => t(`${PREFIX}.fields.${key}`),
  c: (key: string) => t(`${PREFIX}.columns.${key}`),
  v: (key: string, options?: Record<string, unknown>) => t(`${PREFIX}.values.${key}`, options),
  s: (key: string) => t(`${PREFIX}.sections.${key}`),
  none: (key: string): Content => ({ text: t(`${PREFIX}.none.${key}`), style: 'muted' }),
  mark: (key: string) => t(`${PREFIX}.marks.${key}`),
});

const day = (value: string | null | undefined) => (value ? formatDay(value) : '—');

function integrity(t: RecordPdfT, hash: string): Content[] {
  const { v } = helpers(t);
  return [
    { text: v('hash', { hash }), style: 'muted', margin: [0, 10, 0, 0] },
    { text: v('hashNote'), style: 'muted' },
  ];
}

// --- Denetim raporu ---

/** Raporda kanıt eki: kontrol noktasıyla. */
export interface PdiReportAttachment extends ReportAttachment {
  checkpointCode: string;
}

export interface PdiReportInput {
  work: PdiInspectionWork;
  /** Projenin bulguları (denetime ait olanlar basılır); okunamazsa `null`. */
  scope: DrydockScope | null;
  brand: ReportBrand | null;
  /** Basım zamanı, UTC ISO. */
  printedAt: string;
  attachments?: readonly PdiReportAttachment[];
  files?: ReportFiles;
}

export function pdiInspectionReportPdf(t: RecordPdfT, input: PdiReportInput): TDocumentDefinitions {
  const { f, c, v, s, none, mark } = helpers(t);
  const { work } = input;
  const latest = latestByCode(work.results);
  const na = vesselNotApplicable(work.applicability);
  const rows = checkpointRows(work.tree, { latest, na, selected: null, filter: 'all', query: '' });
  const findings = (input.scope?.findings ?? []).filter((x) => x.inspectionId === work.id);
  const attachments = input.attachments ?? [];
  const total = work.progress.total;
  const percent = total.applicable === 0 ? 0 : Math.round((total.done / total.applicable) * 100);
  const kind = work.kind ?? 'pre_docking';

  const general: Content[] = [
    pdfHeading(s('general')),
    pdfFacts([
      [f('vessel'), pdfText(work.vesselName)],
      [f('project'), `${work.projectNo} · ${work.projectTitle}`],
      [f('inspection'), work.inspectionNo],
      [f('kind'), t(`drydock.yard.kinds.${kind}`)],
      [f('openedOn'), day(work.openedOn)],
      [f('closedOn'), day(work.closedOn)],
      [
        f('library'),
        v('libraryVersion', {
          label: work.libraryLabel ?? '—',
          version: work.libraryVersion ?? '—',
        }),
      ],
      [f('status'), t(`drydock.inspectionStates.${work.state}`)],
    ]),
    pdfHeading(s('summary')),
    pdfFacts([
      [f('applicable'), String(total.applicable)],
      [f('inspected'), String(total.done)],
      [f('remaining'), String(total.remaining)],
      [f('adverse'), String(total.adverse)],
      [f('deferred'), String(total.deferred)],
      [f('notApplicable'), String(total.notApplicable)],
      [f('progress'), v('percent', { percent })],
    ]),
  ];

  const results: Content[] = [pdfHeading(s('results'))];
  if (rows.length === 0) results.push(none('results'));
  else {
    results.push(
      pdfTable(
        [
          c('code'),
          c('checkpoint'),
          c('result'),
          c('access'),
          c('inspector'),
          c('inspected'),
          c('revision'),
          c('review'),
        ],
        rows.map((row): TableCell[] => {
          const r = row.latest;
          const review = r?.review;
          return [
            row.checkpoint.code,
            {
              stack: [
                { text: row.checkpoint.name },
                { text: `${row.areaName} › ${row.systemName}`, style: 'muted' },
                ...(r?.notes ? [{ text: r.notes, style: 'muted' }] : []),
              ],
            },
            t(`drydock.work.status.${row.status}`),
            r ? t(`drydock.work.access.${r.access}`) : '—',
            pdfText(r?.inspectorName ?? null),
            r ? pdfLtUtc(r.inspectedAt, r.tzOffsetMin) : '—',
            r ? String(r.revision) : '—',
            r
              ? review
                ? `${pdfText(review.reviewerName)}${review.selfReview ? `\n${v('selfReview')}` : ''}`
                : v('notReviewed')
              : '—',
          ];
        }),
        [52, '*', 52, 46, 54, 74, 22, 54],
      ),
    );
  }

  const measured = rows.flatMap((row) =>
    (row.latest?.measurements ?? []).map((m) => ({ code: row.checkpoint.code, m })),
  );
  const measurements: Content[] = [pdfHeading(s('measurements'))];
  if (measured.length === 0) measurements.push(none('measurements'));
  else {
    measurements.push(
      pdfTable(
        [
          c('code'),
          c('measurement'),
          c('value'),
          c('lower'),
          c('upper'),
          c('reference'),
          c('verdict'),
        ],
        measured.map(({ code, m }) => {
          const unit = m.unit ? ` ${m.unit}` : '';
          const verdict = pdiMeasurementVerdict(m) ?? 'none';
          return [
            code,
            m.label,
            `${m.value}${unit}`,
            m.lower === null ? '—' : `${m.lower}${unit}`,
            m.upper === null ? '—' : `${m.upper}${unit}`,
            pdfText(m.reference),
            t(`drydock.work.verdict.${verdict}`),
          ];
        }),
        [56, '*', 50, 46, 46, 70, 40],
      ),
    );
  }

  const revised = work.results
    .filter((r) => r.revision > 1)
    .sort((a, b) => a.checkpointCode.localeCompare(b.checkpointCode) || a.revision - b.revision);
  const corrections: Content[] = [pdfHeading(s('corrections'))];
  if (revised.length === 0) corrections.push(none('corrections'));
  else {
    corrections.push(
      pdfTable(
        [c('code'), c('revision'), c('result'), c('reason'), c('recordedBy'), c('at')],
        revised.map((r) => [
          r.checkpointCode,
          String(r.revision),
          t(`drydock.work.status.${r.result}`),
          pdfText(r.correctionReason),
          pdfText(r.createdByName),
          pdfLtUtc(r.createdAt, r.tzOffsetMin),
        ]),
        [56, 24, 64, '*', 70, 84],
      ),
    );
  }

  const findingSection: Content[] = [pdfHeading(s('findings'))];
  if (findings.length === 0) findingSection.push(none('findings'));
  else findingSection.push(findingTable(t, findings));

  const evidence: Content[] = [
    pdfHeading(s('attachments')),
    ...reportAttachmentSection(t, attachments, input.files ?? {}, {
      prefix: PREFIX,
      ref: (a) => a.checkpointCode,
    }),
  ];

  const approvals: RecordPdfApproval[] = rows.flatMap((row) => {
    const review = row.latest?.review;
    if (!review) return [];
    return [
      {
        step: v('reviewStep', { code: row.checkpoint.code }),
        name: review.reviewerName,
        role: null,
        at: review.createdAt,
        offsetMin: row.latest?.tzOffsetMin ?? work.tzOffsetMin,
        note: review.selfReview ? v('selfReview') : review.note,
      },
    ];
  });

  const hash = drydockReportHash({
    format: 'felix-pdi-report/1',
    inspection: {
      id: work.id,
      no: work.inspectionNo,
      state: work.state,
      closedOn: work.closedOn ?? null,
      libraryVersionId: work.libraryVersionId,
    },
    applicability: work.applicability.map((a) => [a.checkpointCode, a.applicability]),
    results: work.results.map((r) => ({
      id: r.id,
      code: r.checkpointCode,
      revision: r.revision,
      result: r.result,
      access: r.access,
      review: r.review?.id ?? null,
    })),
    findings: findings.map((x) => [x.id, x.contentHash, x.status]),
    attachments: attachments.map((a) => [a.id, a.sha256, a.voided === true]),
  });

  const marks =
    work.state === 'open'
      ? [mark('provisional')]
      : work.state === 'cancelled'
        ? [mark('cancelled')]
        : [];
  return recordPdf(t, {
    brand: input.brand,
    title: t(`${PREFIX}.${kind === 'after_docking' ? 'afterTitle' : 'pdiTitle'}`),
    number: work.inspectionNo,
    marks,
    subject: `${work.vesselName ?? ''} · ${work.projectNo}`,
    content: [
      ...general,
      ...results,
      ...measurements,
      ...corrections,
      ...findingSection,
      ...evidence,
    ],
    approvalTitle: s('review'),
    approvals,
    trailer: integrity(t, hash),
    printedAt: input.printedAt,
  });
}

function findingTable(t: RecordPdfT, findings: readonly PdiFindingRow[]): Content {
  const { c } = helpers(t);
  return pdfTable(
    [c('findingNo'), c('checkpoint'), c('title'), c('priority'), c('disposition'), c('status')],
    findings.map((x) => [
      x.findingNo,
      x.checkpointCode,
      { stack: [{ text: x.title }, { text: x.location, style: 'muted' }] },
      t(`drydock.scope.priorities.${x.priority}`),
      t(`drydock.scope.dispositions.${x.disposition}`),
      t(`drydock.scope.findingStatus.${x.status}`),
    ]),
    [80, 60, '*', 44, 56, 56],
  );
}

// --- Yard Job List ---

/** Tersane durumu: başlamamışsa onay durumu, başlamışsa icra durumu (Approved … Pending). */
function yardStatus(t: RecordPdfT, job: DrydockJobRow): string {
  const state = job.execution?.state ?? 'not_started';
  if (state === 'not_started') {
    return job.status === 'approved'
      ? t(`${PREFIX}.status.approved`)
      : t(`drydock.scope.jobStatus.${job.status}`);
  }
  return t(`${PREFIX}.status.${state}`);
}

/** İşin işaretleri: onaysız, ek iş, onaydan önce başlayan, değişen, taşınan. */
function jobNotes(t: RecordPdfT, job: DrydockJobRow): Content[] {
  const { v, mark } = helpers(t);
  const out: Content[] = [];
  if (job.status !== 'approved') out.push({ text: mark('notApproved'), style: 'mark' });
  const notes = [
    job.additional ? v('additional') : null,
    job.execution?.provisional ? v('provisional') : null,
    job.changed === 'since_approval' ? v('changed') : null,
    job.carriedFrom ? v('carriedFrom', { job: job.carriedFrom.jobNo }) : null,
    job.carriedTo
      ? v('carriedTo', { job: job.carriedTo.jobNo, project: job.carriedTo.projectNo })
      : null,
  ].filter((x): x is string => x !== null);
  for (const note of notes) out.push({ text: note, style: 'muted' });
  return out;
}

const originOf = (job: DrydockJobRow) =>
  job.origins.map((o) => o.findingNo ?? o.checkpointCode).join(', ') || '—';

export interface DrydockJobListInput {
  scope: DrydockScope;
  brand: ReportBrand | null;
  printedAt: string;
}

export function drydockJobListPdf(t: RecordPdfT, input: DrydockJobListInput): TDocumentDefinitions {
  const { f, c, v, s, none, mark } = helpers(t);
  const { scope } = input;
  const active = scope.jobs.filter((j) => j.state !== 'cancelled');
  const cancelled = scope.jobs.filter((j) => j.state === 'cancelled');
  const p = scope.project;

  const content: Content[] = [
    pdfHeading(s('general')),
    pdfFacts([
      [f('vessel'), pdfText(p.vesselName)],
      [f('project'), `${p.projectNo} · ${p.title}`],
      [f('status'), t(`drydock.states.${p.state}`)],
      [f('dockedOn'), day(p.dockedOn)],
      [f('jobs'), String(active.length)],
      [f('approvedJobs'), String(active.filter((j) => j.status === 'approved').length)],
    ]),
    pdfHeading(s('jobs')),
  ];
  if (active.length === 0) content.push(none('jobs'));
  else {
    content.push(
      pdfTable(
        [
          c('jobNo'),
          c('title'),
          c('trade'),
          c('quantity'),
          c('origin'),
          c('status'),
          c('progress'),
        ],
        active.map((j): TableCell[] => [
          j.jobNo,
          { stack: [{ text: j.title }, { text: j.scope, style: 'muted' }] },
          j.trade ? t(`drydock.scope.trades.${j.trade}`) : '—',
          j.quantity === null ? '—' : `${j.quantity}${j.unit ? ` ${j.unit}` : ''}`,
          originOf(j),
          { stack: [{ text: yardStatus(t, j) }, ...jobNotes(t, j)] },
          j.execution?.percent == null ? '—' : v('percent', { percent: j.execution.percent }),
        ]),
        [70, '*', 50, 36, 66, 74, 34],
      ),
    );
  }
  if (cancelled.length > 0) {
    content.push(
      pdfHeading(s('cancelledJobs')),
      pdfTable(
        [c('jobNo'), c('title'), c('reason')],
        cancelled.map((j) => [j.jobNo, j.title, pdfText(j.cancelReason)]),
        [80, '*', '*'],
      ),
    );
  }

  const approvals: RecordPdfApproval[] = active.flatMap((j) => {
    const decision = j.submissions.at(-1)?.decision;
    if (!decision) return [];
    return [
      {
        step: v('decisionStep', {
          decision: v(`decisions.${decision.decision}`),
          no: j.jobNo,
        }),
        name: decision.deciderName,
        role: null,
        at: decision.createdAt,
        offsetMin: null,
        note: [decision.reason, decision.reauthenticated ? v('reauthenticated') : null]
          .filter(Boolean)
          .join(' · '),
      },
    ];
  });

  const hash = drydockReportHash({
    format: 'felix-yard-job-list/1',
    project: { id: p.id, state: p.state },
    jobs: scope.jobs.map((j) => ({
      id: j.id,
      contentHash: j.contentHash,
      status: j.status,
      state: j.state,
      execution: j.execution?.state ?? null,
      percent: j.execution?.percent ?? null,
      carriedTo: j.carriedTo?.jobId ?? null,
    })),
  });
  const unapproved = active.some((j) => j.status !== 'approved');
  return recordPdf(t, {
    brand: input.brand,
    title: t(`${PREFIX}.jobListTitle`),
    number: p.projectNo,
    marks: unapproved ? [mark('containsNotApproved')] : [],
    subject: `${p.vesselName ?? ''} · ${p.projectNo}`,
    content,
    approvalTitle: s('approval'),
    approvals,
    trailer: integrity(t, hash),
    printedAt: input.printedAt,
  });
}

// --- Kapanış raporu ---

export interface DrydockCloseoutInput {
  project: DrydockProject;
  /** Kapanış önizlemesi (açık projede plan); okunamazsa `null`. */
  closeout: DrydockCloseout | null;
  scope: DrydockScope;
  brand: ReportBrand | null;
  printedAt: string;
}

export function drydockCloseoutPdf(
  t: RecordPdfT,
  input: DrydockCloseoutInput,
): TDocumentDefinitions {
  const { f, c, v, s, none, mark } = helpers(t);
  const { project, scope, closeout } = input;
  const closed = project.state === 'completed';
  const active = scope.jobs.filter((j) => j.state !== 'cancelled');
  const findings = scope.findings.filter((x) => x.state !== 'cancelled');
  const exec = (j: DrydockJobRow) => j.execution?.state ?? 'not_started';
  const yard = [project.yardName, project.yardLocation].filter(Boolean).join(', ');

  const content: Content[] = [
    pdfHeading(s('general')),
    pdfFacts([
      [f('vessel'), pdfText(project.vesselName)],
      [f('project'), `${project.projectNo} · ${project.title}`],
      [f('kind'), t(`drydock.kinds.${project.kind}`)],
      [f('status'), t(`drydock.states.${project.state}`)],
      [f('yard'), pdfText(yard)],
      [f('dockedOn'), day(project.dockedOn)],
      [f('undockedOn'), day(project.undockedOn)],
      [f('closedOn'), day(project.closedOn)],
    ]),
    ...pdfBlock(f('closeNote'), project.closeNote),
    pdfHeading(s('summary')),
    pdfFacts([
      [f('findings'), String(findings.length)],
      [f('closedFindings'), String(findings.filter((x) => x.status === 'closed').length)],
      [f('jobs'), String(active.length)],
      [
        f('verifiedJobs'),
        String(active.filter((j) => j.execution?.verification === 'verified').length),
      ],
      [f('closedJobs'), String(active.filter((j) => exec(j) === 'closed').length)],
      [f('pendingJobs'), String(active.filter((j) => exec(j) === 'pending').length)],
    ]),
  ];

  if (!closed && closeout) {
    const list = (jobs: readonly { jobNo: string }[]) => jobs.map((j) => j.jobNo).join(', ');
    const lines = [
      closeout.openInspections.length > 0
        ? v('openInspections', { list: closeout.openInspections.join(', ') })
        : null,
      closeout.close.length > 0 ? v('planClose', { list: list(closeout.close) }) : null,
      closeout.pending.length > 0 ? v('planPending', { list: list(closeout.pending) }) : null,
      closeout.alreadyPending.length > 0
        ? v('planAlreadyPending', { list: list(closeout.alreadyPending) })
        : null,
      closeout.untouched.length > 0 ? v('planUntouched', { list: list(closeout.untouched) }) : null,
      closeout.awaitingVerification.length > 0
        ? v('awaitingVerification', { list: closeout.awaitingVerification.join(', ') })
        : null,
    ].filter((x): x is string => x !== null);
    content.push(
      pdfHeading(s('plan')),
      ...lines.map((text): Content => ({ text, margin: [0, 0, 0, 2] })),
    );
  }

  content.push(pdfHeading(s('inspections')));
  if (scope.inspections.length === 0) content.push(none('inspections'));
  else {
    content.push(
      pdfTable(
        [c('inspectionNo'), f('kind'), c('status'), f('closedOn')],
        scope.inspections.map((i) => [
          i.inspectionNo,
          t(`drydock.yard.kinds.${i.kind ?? 'pre_docking'}`),
          t(`drydock.inspectionStates.${i.state}`),
          day(i.closedOn),
        ]),
        ['*', 90, 70, 70],
      ),
    );
  }

  content.push(pdfHeading(s('jobs')));
  if (active.length === 0) content.push(none('jobs'));
  else {
    content.push(
      pdfTable(
        [c('jobNo'), c('title'), c('approval'), c('execution'), c('verification')],
        active.map((j): TableCell[] => [
          j.jobNo,
          j.title,
          t(`drydock.scope.jobStatus.${j.status}`),
          { stack: [{ text: yardStatus(t, j) }, ...jobNotes(t, j)] },
          j.execution?.verification ? v(`verdicts.${j.execution.verification}`) : '—',
        ]),
        [70, '*', 60, 90, 56],
      ),
    );
  }

  const pending = active.filter((j) => exec(j) === 'pending');
  content.push(pdfHeading(s('pending')));
  if (pending.length === 0) content.push(none('pending'));
  else {
    content.push(
      pdfTable(
        [c('jobNo'), c('title'), c('carriedTo')],
        pending.map((j) => [
          j.jobNo,
          j.title,
          j.carriedTo ? `${j.carriedTo.jobNo} (${j.carriedTo.projectNo})` : '—',
        ]),
        [80, '*', 120],
      ),
    );
  }

  const open = findings.filter((x) => x.status !== 'closed');
  content.push(pdfHeading(s('openFindings')));
  if (open.length === 0) content.push(none('openFindings'));
  else content.push(findingTable(t, open));

  const approvals: RecordPdfApproval[] = active.flatMap((j) =>
    (j.execution?.verifications ?? []).map((x) => ({
      step: v('verificationStep', { no: j.jobNo }),
      name: x.byName,
      role: v(`verdicts.${x.verdict}`),
      at: x.createdAt,
      offsetMin: null,
      note: x.note,
    })),
  );

  const hash = drydockReportHash({
    format: 'felix-drydock-closeout/1',
    project: {
      id: project.id,
      state: project.state,
      dockedOn: project.dockedOn ?? null,
      undockedOn: project.undockedOn ?? null,
      closedOn: project.closedOn ?? null,
      closeNote: project.closeNote ?? null,
    },
    inspections: scope.inspections.map((i) => [i.id, i.state]),
    jobs: scope.jobs.map((j) => ({
      id: j.id,
      contentHash: j.contentHash,
      status: j.status,
      execution: j.execution?.state ?? null,
      verification: j.execution?.verification ?? null,
      carriedTo: j.carriedTo?.jobId ?? null,
    })),
    findings: scope.findings.map((x) => [x.id, x.contentHash, x.status]),
  });
  return recordPdf(t, {
    brand: input.brand,
    title: t(`${PREFIX}.closeoutTitle`),
    number: project.projectNo,
    marks: closed ? [] : [mark('preview')],
    subject: `${project.vesselName ?? ''} · ${project.projectNo}`,
    content,
    approvalTitle: s('verification'),
    approvals,
    trailer: integrity(t, hash),
    printedAt: input.printedAt,
  });
}
